import { useCallback, useEffect } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import {
    ActionIcon,
    Box,
    Button,
    Divider,
    Flex,
    Group,
    Input,
    Paper,
    Select,
    Space,
    Switch,
    Text,
    Textarea,
    Tooltip,
    useMantineColorScheme,
    useMantineTheme,
} from "@mantine/core";
import { IconGripVertical, IconPlus, IconTrash } from "@tabler/icons-react";
import { QuestionCondition, QuestionType } from "src/graphql/generated/graphql";

import { FormItem } from "../../FormItem";
import { useQuestionnaireFormContext } from "../formContext";
import { Condition } from "./condition";
import { Option } from "./option";

const questionTypesWithOptions = [
    QuestionType.Radio,
    QuestionType.Checkbox,
] as QuestionType[];

export interface QuestionProps {
    index: number;
}

export const Question = ({ index }: QuestionProps) => {
    const { t } = useTranslation(["common"]);

    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();

    const questionnaireForm = useQuestionnaireFormContext();

    const question = questionnaireForm.values?.questions?.[index];
    const isConditional = !!question?.condition;
    const hasOptions = questionTypesWithOptions.indexOf(question?.type) >= 0;

    useEffect(() => {
        if (hasOptions && !question?.options?.length) {
            handleAddOption();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOptions, question?.options?.length]);

    const handleRemove = useCallback(() => {
        questionnaireForm.removeListItem("questions", index);
    }, [index, questionnaireForm]);

    const handleAddOption = useCallback(() => {
        questionnaireForm.insertListItem(`questions.${index}.options`, {
            key: `${question?.key}::${Math.random().toString(36).substring(2)}`,
            text: "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, question?.key]);

    const handleTypeChange = useCallback((index: number, type: QuestionType) => {
        questionnaireForm.setFieldValue(`questions.${index}.type`, type);

        switch (type) {
            case QuestionType.Radio:
            case QuestionType.Checkbox:
                return;
            case QuestionType.Text:
            case QuestionType.Textarea:
            case QuestionType.Number:
            case QuestionType.Date:
            case QuestionType.DateTime:
            case QuestionType.Time:
                questionnaireForm.setFieldValue(`questions.${index}.options`, []);
                return;
        }
    }, [questionnaireForm]);

    return (
        <>
            <Draggable
                draggableId={`${index}`}
                key={`${index}`}
                index={index}
            >
                {(provided) => (
                    <Paper
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                        style={{ ...provided.draggableProps?.style }}
                        bg={colorScheme === "dark" ? theme.colors.dark[7] : "#fff"}
                        p="lg"
                        radius="md"
                    >
                        <Flex align="center" mb="sm">
                            <Text className="questionnaire-question-index" mr="xs" size="md">
                                <Box
                                    className="questionnaire-question-index-bg"
                                    style={{ backgroundColor: theme.colors.gray[1] }}
                                />
                                <Text
                                    weight={500}
                                    color="dark"
                                    className="questionnaire-question-index-val"
                                >
                                    {index + 1}.
                                </Text>
                            </Text>
                            <Input.Label display="block" mr="md">
                                {t("questionnaire_question")}
                            </Input.Label>
                            <Tooltip label={t("questionnaire_question_reorder")} withArrow>
                                <ActionIcon
                                    {...provided.dragHandleProps}
                                    mr="xs"
                                    color="blue.400"
                                    variant="subtle"
                                >
                                    <IconGripVertical size="1em" />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t("questionnaire_question_remove")} withArrow>
                                <ActionIcon
                                    mr="xs"
                                    color="red"
                                    variant="subtle"
                                    onClick={handleRemove}
                                >
                                    <IconTrash size="1em" />
                                </ActionIcon>
                            </Tooltip>
                        </Flex>
                        <Flex align="stretch" mb="sm">
                            <Flex direction="column" align="flex-start" w="auto" mr="md">
                                <Select
                                    {...questionnaireForm.getInputProps(`questions.${index}.type`)}
                                    mb="xs"
                                    label={t("questionnaire_question_type")}
                                    placeholder={t("questionnaire_question_type")}
                                    data={[
                                        QuestionType.Radio,
                                        QuestionType.Checkbox,
                                        QuestionType.Text,
                                        QuestionType.Textarea,
                                        QuestionType.Number,
                                        QuestionType.Date,
                                        QuestionType.Time,
                                        QuestionType.DateTime,
                                    ].map((value) => ({
                                        value,
                                        label: t(`questionnaire_question_type_${value}`),
                                    }))}
                                    onChange={(type: QuestionType) => handleTypeChange(index, type)}
                                />
                                <Group spacing="xs" mr="1rem">
                                    <FormItem label={t("questionnaire_question_isoptional")}>
                                        <Switch
                                            {...questionnaireForm.getInputProps(`questions.${index}.isOptional`)}
                                            checked={question?.isOptional}
                                            onChange={event =>
                                                questionnaireForm.setFieldValue(
                                                    `questions.${index}.isOptional`,
                                                    event?.target?.checked
                                                )}
                                        />
                                    </FormItem>
                                    <FormItem label={t("questionnaire_question_isconditional")}>
                                        <Switch
                                            {...questionnaireForm.getInputProps(`questions.${index}.isOptional`)}
                                            checked={isConditional}
                                            onChange={event => {
                                                if (event?.target?.checked) {
                                                    const condition: QuestionCondition = {
                                                        dependsOn: null,
                                                        type: null,
                                                        requiredValue: null,
                                                    };

                                                    questionnaireForm.setFieldValue(`questions.${index}.condition`, condition);
                                                    return;
                                                }

                                                questionnaireForm.setFieldValue(`questions.${index}.condition`, null);
                                            }}
                                        />
                                    </FormItem>
                                </Group>
                            </Flex>
                            <Textarea
                                {...questionnaireForm.getInputProps(`questions.${index}.text`)}
                                label={t("questionnaire_question_text")}
                                placeholder={t("questionnaire_question_text")}
                                style={{ flex: 1 }}
                            />
                        </Flex>
                        {isConditional && (
                            <Paper
                                p="md"
                                mb="sm"
                                radius="lg"
                                bg={colorScheme === "dark" ?
                                    theme.colors.dark[8] : theme.colors.gray[0]}
                                withBorder
                            >
                                <Condition questionIndex={index} />
                            </Paper>
                        )}
                        {hasOptions && (
                            <FormItem label={t("questionnaire_question_options")}>
                                <Space h="4px" />
                                {/* <DragDropContext
                                    onDragEnd={({ source, destination }) =>
                                        questionnaireForm.reorderListItem(`questions.${index}.options`, {
                                            from: source.index,
                                            to: destination.index,
                                        })}
                                > */}
                                <Droppable
                                    droppableId={`${questionnaireForm.values?.questions[index].key}`}
                                    direction="vertical"
                                    type={`option-${index}`}
                                >
                                    {(provided) => (
                                        <Box {...provided.droppableProps} ref={provided.innerRef}>
                                            {(question?.options || []).map((option, optionIndex) => (
                                                <Option
                                                    key={option?.key}
                                                    questionIndex={index}
                                                    index={optionIndex}
                                                    hideRemoveButton={question?.options?.length === 1}
                                                />
                                            ))}
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                                {/* </DragDropContext> */}
                                <Group position="right">
                                    <Button
                                        leftIcon={<IconPlus />}
                                        onClick={() =>
                                            handleAddOption()}
                                    >
                                        {t("questionnaire_question_option_add")}
                                    </Button>
                                </Group>
                            </FormItem>
                        )}
                    </Paper>
                )}
            </Draggable>
            <Divider color={colorScheme === "dark" ? "gray.9" : "gray.1"} />
        </>
    );
};
